import React from 'react';
import { Target, Heart, Globe2, Users } from 'lucide-react';
import FinalCTA from '../components/FinalCTA';
import { STATS } from '../data/mock';

const VALUES = [
  { icon: Target, title: 'Outcomes over output', desc: 'We measure success by your KPIs\u2014revenue saved, hours reclaimed, customers delighted.' },
  { icon: Heart, title: 'Care, not call-centre', desc: 'Every assistant is trained, mentored and treated as part of your team\u2014not a ticket queue.' },
  { icon: Globe2, title: 'India-first, globally relevant', desc: 'We understand Bharat\u2019s realities and apply global operational best practices.' },
  { icon: Users, title: 'People + AI', desc: 'We don\u2019t replace humans with AI. We augment them so they do their best work.' },
];

const TEAM = [
  { name: 'Aditya Kapoor', role: 'CEO & Founder', city: 'Bengaluru' },
  { name: 'Sneha Iyer', role: 'Head of Operations', city: 'Mumbai' },
  { name: 'Rahul Bhatt', role: 'VP, AI & Automation', city: 'Delhi' },
  { name: 'Ankita Das', role: 'Director, People', city: 'Guwahati' },
];

const initials = (name) => name.split(' ').map((n) => n[0]).join('');

const AboutHero = () => (
  <section className="relative pt-36 pb-16 overflow-hidden">
    <div className="absolute inset-0 bg-radial-glow pointer-events-none" />
    <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
    <div className="relative max-w-4xl mx-auto px-6 text-center">
      <p className="text-[#1AB6FF] font-semibold uppercase tracking-[0.2em] text-xs mb-4">About Us</p>
      <h1 className="font-display font-bold text-5xl md:text-6xl leading-[1.05]">
        We&rsquo;re building the operational <span className="gradient-text">backbone for Bharat.</span>
      </h1>
      <p className="mt-6 text-white/65 text-lg max-w-2xl mx-auto">
        Geekynd India is the India-focused arm of Geekynd &mdash; helping ambitious founders, agencies and enterprises run leaner with AI-powered teams.
      </p>
    </div>
  </section>
);

const MissionSection = () => (
  <section className="py-16">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <h2 className="font-display font-bold text-4xl md:text-5xl leading-tight">
          Our <span className="gradient-text">Mission.</span>
        </h2>
        <p className="mt-5 text-white/65 leading-relaxed">
          Indian businesses are scaling faster than ever&mdash;but operationally, most are still stuck in spreadsheets, WhatsApp groups and late-night firefighting. We believe every founder deserves a world-class ops engine without the world-class price tag.
        </p>
        <p className="mt-4 text-white/65 leading-relaxed">
          That&rsquo;s why we built Geekynd India: dedicated remote teams, AI agents and SOP-driven workflows that plug into your business and start compounding from day one.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {STATS.map((s) => (
          <div key={s.label} className="card-tech rounded-2xl p-6">
            <p className="font-display font-bold text-4xl gradient-text">{s.value}</p>
            <p className="mt-2 text-white/55 text-sm">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ValueCard = ({ value }) => (
  <div className="card-tech rounded-2xl p-6">
    <div className="w-12 h-12 rounded-lg bg-[#1AB6FF]/10 border border-[#1AB6FF]/25 flex items-center justify-center mb-4">
      <value.icon size={22} className="text-[#1AB6FF]" />
    </div>
    <h3 className="font-display font-semibold text-lg text-white">{value.title}</h3>
    <p className="mt-2 text-white/55 text-sm">{value.desc}</p>
  </div>
);

const ValuesSection = () => (
  <section className="py-16">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <h2 className="font-display font-bold text-3xl md:text-4xl text-center">
        What we <span className="gradient-text">stand for.</span>
      </h2>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {VALUES.map((v) => (
          <ValueCard key={v.title} value={v} />
        ))}
      </div>
    </div>
  </section>
);

const TeamCard = ({ member }) => (
  <div className="card-tech rounded-2xl p-6 text-center">
    <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#1AB6FF] to-[#0d6a99] flex items-center justify-center text-2xl font-display font-bold text-[#04070d]">
      {initials(member.name)}
    </div>
    <p className="mt-4 font-display font-semibold text-white">{member.name}</p>
    <p className="text-sm text-white/55">{member.role}</p>
    <p className="text-xs text-[#1AB6FF] mt-1">{member.city}</p>
  </div>
);

const TeamSection = () => (
  <section className="py-16">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <h2 className="font-display font-bold text-3xl md:text-4xl text-center">
        Leadership <span className="gradient-text">team.</span>
      </h2>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {TEAM.map((m) => (
          <TeamCard key={m.name} member={m} />
        ))}
      </div>
    </div>
  </section>
);

const AboutPage = () => (
  <div>
    <AboutHero />
    <MissionSection />
    <ValuesSection />
    <TeamSection />
    <FinalCTA />
  </div>
);

export default AboutPage;
